import {
  ExperiencePerformanceTypes,
  ExperienceTypes,
  UFOExperience,
  UFOExperienceState,
} from '@atlassian/ufo';
import { getDatabaseSizeMetrics } from '@databases/analytics';
import { useEffect, useMemo, useRef } from 'react';
import {
  unstable_IdlePriority as idlePriority,
  unstable_scheduleCallback as scheduleCallback,
} from 'scheduler';

import { coinflip } from './coinflip';

type CustomData = {
  [key: string]: string | number | boolean | CustomData | undefined;
};

type KeyboardEvent = {
  key: string;
  timeStamp: number;
};

interface TypingPerformanceMetadata extends CustomData {
  typing: {
    min: number;
    max: number;
    avg: number;
    count: number;
    below50count: number;
    compute: number;
    typeTracingMethod: string;
  };
  databaseSizeMetrics: { entriesCount: number; viewsCount: number };
}

const getTypingMetadata = (
  times: Float64Array | number[],
  computeStartTime: number,
  typingPerformanceTracingMethod: string
) => {
  let min = 0;
  let max = 0;
  let avg = 0;
  let index = 0;
  let below50count = 0;

  times.forEach((time) => {
    if (time !== 0) {
      avg = (avg * index + time) / ++index;

      if (time > max) {
        max = time;
      }

      if (min === 0 || time < min) {
        min = time;
      }

      if (time < 50) {
        below50count++;
      }
    }
  });

  return {
    typing: {
      min,
      max,
      avg,
      count: index,
      below50count,
      compute: performance.now() - computeStartTime,
      typeTracingMethod: typingPerformanceTracingMethod,
    },
  };
};

function typingPerformanceTracingTimeout(
  element: HTMLElement,
  experienceFactory: () => UFOExperience,
  _: number
) {
  let tsubmit: ReturnType<typeof setTimeout> | undefined;
  let times: number[] = [];
  let isInteractionInitialised = false;

  const start = () => {
    isInteractionInitialised = true;
  };

  const publishEvent = async (times: number[]) => {
    const computeStartTime = performance.now();
    const data = getTypingMetadata(times, computeStartTime, 'timeout');
    const experience = experienceFactory();
    await experience.transition(UFOExperienceState.STARTED, 0);

    const { viewsCount, entriesCount } = getDatabaseSizeMetrics();

    experience.addMetadata({
      ...data,
      databaseSizeMetrics: {
        entriesCount,
        viewsCount,
      },
    } as TypingPerformanceMetadata);

    await experience.transition(UFOExperienceState.SUCCEEDED, data.typing.avg);
  };

  const end = () => {
    isInteractionInitialised = false;

    const timesCopy = times;

    times = [];

    scheduleCallback(idlePriority, () => {
      publishEvent(timesCopy);
    });
  };

  const onKeyPressHandler = async (event: KeyboardEvent) => {
    if (!isInteractionInitialised) {
      await start();
    }

    const startTime = event.timeStamp;

    setTimeout(() => {
      const endTime = performance.now();
      const time = endTime - startTime;

      times.push(time);

      clearTimeout(tsubmit);

      tsubmit = setTimeout(end, 2000); // debounce
    }, 0);
  };

  element.addEventListener('keypress', onKeyPressHandler);

  return () => {
    element.removeEventListener('keypress', onKeyPressHandler);
  };
}

function typingPerformanceTracingTimeoutNoAlloc(
  element: HTMLElement,
  experienceFactory: () => UFOExperience,
  _: number
) {
  let tsubmit: ReturnType<typeof setTimeout> | undefined;
  let isInteractionInitialised = false;
  let min = 0;
  let max = 0;
  let below50count = 0;
  let avg = 0;
  let count = 0;

  const resetStats = () => {
    min = 0;
    max = 0;
    below50count = 0;
    avg = 0;
    count = 0;
  };

  const publishEvent = async (data: {
    typing: {
      min: number;
      max: number;
      avg: number;
      count: number;
      below50count: number;
      typeTracingMethod: string;
    };
  }) => {
    const experience = experienceFactory();
    await experience.transition(UFOExperienceState.STARTED, 0);
    const { viewsCount, entriesCount } = getDatabaseSizeMetrics();

    experience.addMetadata({
      ...data,
      databaseSizeMetrics: {
        entriesCount,
        viewsCount,
      },
    } as TypingPerformanceMetadata);

    await experience.transition(UFOExperienceState.SUCCEEDED, data.typing.avg);
  };

  const start = () => {
    isInteractionInitialised = true;
  };

  const end = () => {
    isInteractionInitialised = false;

    const typingMetaData = {
      typing: {
        min,
        max,
        avg,
        count,
        below50count,
        typeTracingMethod: 'timeoutNoAlloc',
      },
    };

    resetStats();
    scheduleCallback(idlePriority, () => {
      publishEvent(typingMetaData);
    });
  };

  const onKeyPressHandler = async (event: KeyboardEvent) => {
    if (!isInteractionInitialised) {
      await start();
    }

    const startTime = event.timeStamp;
    setTimeout(() => {
      const endTime = performance.now();
      const time = endTime - startTime;
      if (time !== 0) {
        avg = (avg * count + time) / ++count;
        if (time > max) {
          max = time;
        }
        if (min === 0 || time < min) {
          min = time;
        }
        if (time < 50) {
          below50count++;
        }
      }
      clearTimeout(tsubmit);
      tsubmit = setTimeout(end, 2000); // debounce
    }, 0);
  };

  element.addEventListener('keypress', onKeyPressHandler);
  return () => {
    element.removeEventListener('keypress', onKeyPressHandler);
  };
}

function typingPerformanceTracingMutationObserver(
  element: HTMLElement,
  experienceFactory: () => UFOExperience,
  _: number
) {
  let eventTime = -1;
  let isInteractionInitialised = false;
  let count = 0;
  const experience = experienceFactory();
  let tsubmit: ReturnType<typeof setTimeout> | undefined;
  const keyLimit = 20;
  const times = new Float64Array(keyLimit);

  const resetTimesArray = () => {
    times.fill(0);
  };

  const resetEventTime = () => {
    eventTime = -1;
  };

  const publishEvent = async (times: Float64Array, computeStartTime: number) => {
    const data = getTypingMetadata(times, computeStartTime, 'mutationObserver');
    const experience = experienceFactory();
    await experience.transition(UFOExperienceState.STARTED, 0);
    experience.addMetadata(data);
    await experience.transition(UFOExperienceState.SUCCEEDED, data.typing.avg);
  };

  const start = async () => {
    await experience.start();
    isInteractionInitialised = true;
  };

  const end = () => {
    isInteractionInitialised = false;
    if (count === 0) {
      return;
    }
    const timesCopy = new Float64Array(times);
    const countCopy = count;
    resetTimesArray();
    resetEventTime();
    count = 0;
    scheduleCallback(idlePriority, () => {
      const computeStartTime = performance.now();
      const timesSubarray = timesCopy.subarray(0, countCopy);
      publishEvent(timesSubarray, computeStartTime);
    });
  };

  const onKeyPressHandler = async (event: KeyboardEvent) => {
    if (!isInteractionInitialised) {
      await start();
    }
    eventTime = event.timeStamp;

    setTimeout(() => {
      clearTimeout(tsubmit);
      tsubmit = setTimeout(end, 2000); // debounce
    }, 0);
  };

  const mo = new MutationObserver((events) => {
    const moTime = performance.now();

    if (eventTime === -1) {
      return;
    }
    if (count > keyLimit - 1) {
      end();
      return;
    }

    const startTime = eventTime;
    resetEventTime();
    events.forEach(() => {
      requestAnimationFrame((firstFrameTime) => {
        if (firstFrameTime < moTime) {
          requestAnimationFrame((secondFrameTime) => {
            times[count] = secondFrameTime - startTime;
            count++;
          });
        } else {
          times[count] = firstFrameTime - startTime;
          count++;
        }
      });
    });
  });

  mo.observe(element, {
    attributeFilter: ['value'],
    attributes: true,
    characterData: true,
    childList: true,
    subtree: true,
  });

  element.addEventListener('keypress', onKeyPressHandler);
  return () => {
    element.removeEventListener('keypress', onKeyPressHandler);
    mo.disconnect();
  };
}

const typingPerformanceTracingMethods = {
  timeout: typingPerformanceTracingTimeout,
  timeoutNoAlloc: typingPerformanceTracingTimeoutNoAlloc,
  mutationObserver: typingPerformanceTracingMutationObserver,
};

export function useTypingPerformance<T extends HTMLElement>(
  name: string,
  rate = 1,
  method: keyof typeof typingPerformanceTracingMethods = 'timeout'
) {
  const ref = useRef<T | null>(null);

  const experienceFactory = useMemo(
    () => () => {
      return new UFOExperience(name, {
        type: ExperienceTypes.Operation,
        performanceType: ExperiencePerformanceTypes.InlineResult,
      });
    },
    [name]
  );

  useEffect(() => {
    if (coinflip(rate) && ref.current != null) {
      return typingPerformanceTracingMethods[method](
        ref.current,
        experienceFactory,
        rate
      );
    }
  }, [rate, method, experienceFactory]);

  return ref;
}
